import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Hospital, Lock, User as UserIcon } from "@phosphor-icons/react";

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      await login(username.trim(), password);
      navigate("/");
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const quickFill = (u, p) => { setUsername(u); setPassword(p); };

  return (
    <div className="min-h-screen flex auth-bg">
      {/* Left brand panel */}
      <div className="hidden lg:flex w-1/2 flex-col justify-between p-12 relative z-10">
        <div className="flex items-center gap-2.5">
          <div className="w-10 h-10 rounded-md bg-blue-600 flex items-center justify-center">
            <Hospital weight="fill" size={22} className="text-white" />
          </div>
          <div>
            <div className="font-extrabold text-slate-900 text-lg tracking-tight" style={{ fontFamily: "Manrope" }}>HK Manager</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Hospital Cleaning Operations</div>
          </div>
        </div>

        <div className="max-w-md">
          <div className="label-small mb-3 text-blue-700">Enterprise Housekeeping Platform</div>
          <h1 className="text-5xl font-extrabold text-slate-900 leading-[1.05] tracking-tight" style={{ fontFamily: "Manrope" }}>
            Cleaner hospitals.<br />
            <span className="text-blue-600">Verified outcomes.</span>
          </h1>
          <p className="mt-5 text-base text-slate-600 leading-relaxed">
            Real-time visibility into cleaning compliance, SLA adherence, and supervisor verification across every hospital, building, floor and area.
          </p>

          <div className="mt-10 grid grid-cols-3 gap-4 max-w-sm">
            <div className="border-l-2 border-blue-600 pl-3">
              <div className="text-3xl font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>5K+</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mt-1">Concurrent users</div>
            </div>
            <div className="border-l-2 border-blue-600 pl-3">
              <div className="text-3xl font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>99.9%</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mt-1">Uptime</div>
            </div>
            <div className="border-l-2 border-blue-600 pl-3">
              <div className="text-3xl font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>ISO</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mt-1">27001 Aligned</div>
            </div>
          </div>
        </div>

        <div className="text-xs text-slate-500">© 2026 HK Manager — Enterprise Edition</div>
      </div>

      {/* Right login form */}
      <div className="flex-1 flex items-center justify-center p-6 relative z-10">
        <div className="w-full max-w-md bg-white border border-slate-200 rounded-md p-8 md:p-10" data-testid="login-card">
          <div className="lg:hidden flex items-center gap-2.5 mb-8">
            <div className="w-10 h-10 rounded-md bg-blue-600 flex items-center justify-center">
              <Hospital weight="fill" size={22} className="text-white" />
            </div>
            <div className="font-extrabold text-slate-900 text-lg" style={{ fontFamily: "Manrope" }}>HK Manager</div>
          </div>

          <div className="label-small text-blue-700 mb-2">Sign In</div>
          <h2 className="text-3xl font-extrabold text-slate-900 mb-1 tracking-tight" style={{ fontFamily: "Manrope" }}>Welcome back</h2>
          <p className="text-sm text-slate-600 mb-8">Access your housekeeping workspace.</p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="label-small block mb-1.5">Username</label>
              <div className="relative">
                <UserIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  data-testid="login-username-input"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  autoFocus
                  className="w-full pl-9 pr-3 py-2.5 border border-slate-200 rounded-md text-sm focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600 transition-colors"
                  placeholder="admin"
                />
              </div>
            </div>
            <div>
              <label className="label-small block mb-1.5">Password</label>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  data-testid="login-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full pl-9 pr-3 py-2.5 border border-slate-200 rounded-md text-sm focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600 transition-colors"
                  placeholder="••••••••"
                />
              </div>
            </div>

            {err && (
              <div data-testid="login-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{err}</div>
            )}

            <button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 rounded-md transition-colors disabled:opacity-60"
            >
              {loading ? "Authenticating…" : "Sign in"}
            </button>
          </form>

        </div>
      </div>
    </div>
  );
}
